import { type ComponentType, memo, useCallback, useEffect, useMemo, useRef } from 'react';

import { AnnotationAvatarButton } from '~/components/AnnotationsV2/AnnotationsAvatarsContainer/AnnotationAvatarContainer/components/AnnotationAvatarButton';
import { type AnnotationCommentDisplayProps } from '~/components/AnnotationsV2/AnnotationsAvatarsContainer/shared/annotationCommentTypes';
import { getAnnotationCommentPos } from '~/components/AnnotationsV2/shared/annotationUtils';
import { type ClipDiscussion } from '~/components/AnnotationsV2/shared/types';
import { useDiscussionPanelContextSelector } from '~/components/AssetModal/shared/context/DiscussionsPanelContext/DiscussionsPanelContext';
import { type DiscussionsPanelContextValue } from '~/components/AssetModal/shared/context/DiscussionsPanelContext/discussionsPanelContextTypes';
import { BOUNDING_BOX_AVATAR_SIZE } from '~/components/BoundingBox/components/BoundingBoxAvatar';
import { useBoundingBoxTextPosition } from '~/components/BoundingBox/hooks/useBoundingBoxTextPosition';
import { type PopperOptions, usePopperContainer } from '~/hooks/usePopperContainer';
import { isViewingDiscussionWithId } from '~/store/router/selectors';
import { useAirSelector } from '~/utils/ReduxUtils';

/** Horizontal gap between the annotation avatar and its floating comment popper. */
const COMMENT_POPPER_GAP_X = 26;
/** Vertical gap between the avatar's bounding box and the comment popper. */
const COMMENT_POPPER_GAP_Y = 8;

export type AnnotationAvatarWithCommentProps = {
  discussion: ClipDiscussion;
  onAvatarClick: (discussion: ClipDiscussion) => void;

  containerWidth: number;
  containerHeight: number;
  containerLeft: number;
  containerTop: number;

  canEdit: boolean;
  canDelete: boolean;
  canResolve: boolean;
  canShowMentionsButton: boolean;
  canReply: boolean;

  /** The surface's floating annotation comment — binds the surface's comment delete around the shared comment body. */
  AnnotationComment: ComponentType<AnnotationCommentDisplayProps>;
};

/**
 * V2: AnnotationAvatarWithComment component that supports multiple annotations per discussion
 * Uses the first annotation in the annotations array for comment positioning
 */
export const AnnotationAvatarWithComment = memo(
  ({
    containerHeight,
    containerLeft,
    containerWidth,
    containerTop,
    onAvatarClick,
    discussion,
    canResolve,
    canReply,
    canShowMentionsButton,
    canEdit,
    canDelete,
    AnnotationComment,
  }: AnnotationAvatarWithCommentProps) => {
    const containerRef = useRef(null);
    const isActive = useAirSelector((st) => isViewingDiscussionWithId(st, discussion.id));

    const isReplyingCurrentDiscussionIdSelector = useCallback(
      (state: DiscussionsPanelContextValue) => state.replyingDiscussionId === discussion.id,
      [discussion.id],
    );

    const isReplyingToDiscussion = useDiscussionPanelContextSelector(isReplyingCurrentDiscussionIdSelector);

    // V2: Use annotations array for position calculation (uses first annotation)
    const { minX, maxX, maxY } = useMemo(
      () => getAnnotationCommentPos(discussion.annotations),
      [discussion.annotations],
    );

    const { offsetLeft, offsetBottom, position } = useBoundingBoxTextPosition({
      startX: minX,
      endY: maxY,
      endX: maxX,
      containerLeft,
      containerWidth,
      containerHeight,
    });

    const popperOptions = useMemo<PopperOptions>(
      () => ({
        modifiers: [
          {
            placement: 'left-start',
            name: 'offset',
            options: {
              offset: ({ popper: { width } }: { popper: { width: number } }) => {
                return [
                  position.includes('left') ? -width / 2 - COMMENT_POPPER_GAP_X : width / 2 + COMMENT_POPPER_GAP_X,
                  -BOUNDING_BOX_AVATAR_SIZE - COMMENT_POPPER_GAP_Y,
                ];
              },
            },
          },
        ],
      }),
      [position],
    );

    const buttonPos = useMemo(() => {
      if (position.includes('left')) {
        return {
          left: offsetLeft - BOUNDING_BOX_AVATAR_SIZE + 2,
          bottom: offsetBottom,
        };
      } else {
        return {
          left: offsetLeft + 0,
          bottom: offsetBottom,
        };
      }
    }, [offsetBottom, offsetLeft, position]);

    const { PopperContainer, popperInstance } = usePopperContainer({
      containerRef,
      usePortal: true,
      options: popperOptions,
    });

    // biome-ignore lint/correctness/useExhaustiveDependencies: the popper's placement derives from the container geometry through DOM measurement (useBoundingBoxTextPosition), so update() must re-run when these props change even though the effect body never reads them — a data flow the rule cannot see.
    useEffect(() => {
      popperInstance?.current?.update();
    }, [containerHeight, containerWidth, containerLeft, popperInstance, containerTop]);

    const comment = useMemo(
      () =>
        isActive && !isReplyingToDiscussion ? (
          <PopperContainer>
            <AnnotationComment
              canEdit={canEdit}
              canReply={canReply}
              canResolve={canResolve}
              canDelete={canDelete}
              canShowMentionsButton={canShowMentionsButton}
              discussion={discussion}
              position={position}
            />
          </PopperContainer>
        ) : null,
      [
        AnnotationComment,
        PopperContainer,
        canDelete,
        canEdit,
        canReply,
        canResolve,
        canShowMentionsButton,
        discussion,
        isActive,
        isReplyingToDiscussion,
        position,
      ],
    );

    return (
      // biome-ignore lint/a11y/noStaticElementInteractions: the handlers only stop propagation so portal'd menu clicks don't close the popper — there is no user-facing interaction here to expose a role for.
      // biome-ignore lint/a11y/useKeyWithClickEvents: same — no click behavior exists to mirror on the keyboard; the wrapper only shields bubbling.
      <div
        className="absolute flex"
        ref={containerRef}
        style={{
          ...buttonPos,
          pointerEvents: 'all',
        }}
        /**
         * Stop click propagation to prevent the popper from closing when clicking
         * on any the menu items. This is because the menu are rendered inside a
         * portal and the popper is rendered inside the annotation container.
         */
        onClick={(event) => event.stopPropagation()}
        onMouseDown={(event) => event.stopPropagation()}
      >
        {position.includes('left') && comment}
        <AnnotationAvatarButton discussion={discussion} onAvatarClick={onAvatarClick} position={position} />
        {position.includes('right') && comment}
      </div>
    );
  },
);

AnnotationAvatarWithComment.displayName = 'AnnotationAvatarWithComment';
